import React from 'react';
import isNil from 'lodash.isnil';
import isEmpty from 'lodash.isempty';
import get from 'lodash.get';
import { compareAsc, getTime, isAfter, parseISO } from 'date-fns';
import { MiniAimline } from '@amplify/mini-aimline';
import maxBy from 'lodash.maxby';
import uniq from 'lodash.uniq';
import last from 'lodash.last';
import { StudentInfoCell, TextCell, TwoPartHeaderCell } from '../../components/cell/shared/Cells';
import { CLASS_NAME, INDEX as CELL_INDEX } from '../../constants/shared/CellConstants';
import { COMPOSITE, PERIOD } from '../../constants/shared/CommonConstants';
import {
    getCommonGradeHeaderCell,
    getDividedColSpan,
    getProgressGridHeader,
    getTwoPartHeaderCell,
} from '../../features/progress/shared/progressHelper';
import {
    INDEX as PM_INDEX,
    KEY,
    PM_COL_SPAN_SUBHEADER_CELL,
    PM_SCORES_NUMBER,
    TITLE as PM_TITLES,
} from '../../constants/shared/PMConstants';
import { MEASURE } from '../../constants/shared/MeasuresConstants';

const { SKILL_KEY, PERIOD_KEY } = KEY;
const { SKILL_TITLE, PM_TITLE } = PM_TITLES;
const { SORTING_SPACE, SECOND_COLUMN, LEFT_ALIGN, MIDDLE_COLUMN } = CLASS_NAME;
const { EOY, AFTER_EOY } = PERIOD;
const { FIRST_GRID_ROW, START_FIRST_COL_SPAN_INDEX } = CELL_INDEX;
const { LAST_GRID_HEADER, LAST_SCORES_START, LAST_SCORES_END } = PM_INDEX;

export const getStartPmPeriod = pmPeriod => (pmPeriod === AFTER_EOY ? EOY : pmPeriod.split('_')[0]);

export const getEndPmPeriod = pmPeriod => (pmPeriod === AFTER_EOY ? null : pmPeriod.split('_')[1]);

export const getCursorPointerClassName = resultId => `${isNil(resultId) ? '' : 'pointer'}`;

export const getFormName = (measure, grade, period, formNumber, formGrade) => {
    const isPmPeriod = period === PERIOD.PM;
    if (isPmPeriod && measure === MEASURE.TRC) return '';
    const formNumberText = isNil(formNumber) ? '' : `-${formNumber}`;
    if (grade !== formGrade) {
        return `Gr. ${formGrade}${formNumberText}`;
    }
    return `${period}${isPmPeriod ? formNumberText : ''}`;
};

export const handleScoreClick = (
    resultId,
    showPencil,
    openProbeDetails,
    openBasicComprehensionWebEntry,
) => {
    if (showPencil) {
        return openBasicComprehensionWebEntry;
    }
    return resultId ? () => openProbeDetails(resultId) : () => {};
};

export const getAimline = (measure, startPoint, middlePoints, endPoint, showAimline) =>
    showAimline ? (
        <TextCell
            content={
                <MiniAimline
                    startPoint={startPoint}
                    middlePoints={middlePoints.filter(point =>
                        isAfter(parseISO(point.date), parseISO(get(startPoint, 'date'))),
                    )}
                    endPoint={endPoint}
                />
            }
        />
    ) : (
        <TextCell isDataCell classNames="empty-cell" />
    );

export const getPmHeaderRow = (pmPeriod, grade) => ({
    ...getCommonGradeHeaderCell(grade),
    ...getTwoPartHeaderCell(
        'composite',
        SECOND_COLUMN,
        '',
        getStartPmPeriod(pmPeriod),
        COMPOSITE,
        true,
    ),
    ...getTwoPartHeaderCell(SKILL_KEY, LEFT_ALIGN, SORTING_SPACE, '', SKILL_TITLE),
    ...getTwoPartHeaderCell(PERIOD_KEY, '', SORTING_SPACE, '', getStartPmPeriod(pmPeriod), false),
    ...getTwoPartHeaderCell('pmScore1'),
    ...getTwoPartHeaderCell('pmScore2'),
    ...getTwoPartHeaderCell('pmScore3'),
    ...getTwoPartHeaderCell('nextPeriod', '', SORTING_SPACE, getEndPmPeriod(pmPeriod), 'Goal'),
    ...getTwoPartHeaderCell('goalSet', '', SORTING_SPACE, '', 'Goal Set'),
    ...getTwoPartHeaderCell('aimline', '', SORTING_SPACE, 'Aimline to', 'Goal Set'),
    ...getTwoPartHeaderCell('lastAssessed', MIDDLE_COLUMN, SORTING_SPACE, 'Last', 'Assessed'),
});

export const getTeacher = (teacher, className, isDataCell) => (
    <TextCell
        classNames={`${className} ${LEFT_ALIGN}`}
        isDataCell={isDataCell}
        content={
            isEmpty(teacher) ? null : (
                <StudentInfoCell
                    classNames="first-column"
                    firstName={teacher.firstName}
                    lastName={teacher.lastName}
                />
            )
        }
    >
        {isEmpty(teacher) ? '' : `${teacher.lastName} ${teacher.firstName}`}
    </TextCell>
);
/**
 * Only the latest Basic Comprehension web-entry status is editable
 */
export const isStatusLastWebEntry = (status, pmStatuses, editableMeasure) => {
    const lastWebEntryStatus =
        status.measure === editableMeasure
            ? maxBy(
                  pmStatuses.filter(pmStatus => pmStatus.isWebEntry),
                  pmStatus => getTime(parseISO(pmStatus.date)),
              )
            : {};
    return status === lastWebEntryStatus;
};

const getLastSortedPmStatuses = (pmStatusesByMeasure, grade, isStatusEditable) =>
    pmStatusesByMeasure
        .filter(status => status.formGrade === grade)
        .map(status => ({
            ...status,
            ...(isStatusEditable(status, pmStatusesByMeasure) && { withPencil: true }),
        }))
        .sort((status1, status2) => compareAsc(parseISO(status1.date), parseISO(status2.date)))
        .slice(-PM_SCORES_NUMBER);

export const getRowsData = (pmStatuses, isStatusEditable) => {
    const pmStatusesByMeasuresGrades = [];
    const uniqMeasures = uniq(pmStatuses.map(status => status.measure));
    uniqMeasures.forEach(measure => {
        const pmStatusesByMeasure = pmStatuses.filter(pmStatus => pmStatus.measure === measure);
        const gradesByMeasure = uniq(pmStatusesByMeasure.map(status => status.formGrade));
        gradesByMeasure.forEach(grade => {
            const lastSortedPmStatuses = getLastSortedPmStatuses(
                pmStatusesByMeasure,
                grade,
                isStatusEditable,
            );
            pmStatusesByMeasuresGrades.push({
                measure,
                grade,
                pmStatuses: lastSortedPmStatuses,
                lastDate: last(lastSortedPmStatuses).date,
            });
        });
    });
    return pmStatusesByMeasuresGrades;
};

export const buildPmProgressHeader = () =>
    getProgressGridHeader(FIRST_GRID_ROW, START_FIRST_COL_SPAN_INDEX, LAST_GRID_HEADER, PM_TITLE);

export const getProgressDividedColSpan = resultsByGrades =>
    getDividedColSpan(
        resultsByGrades,
        LAST_SCORES_START,
        LAST_SCORES_END,
        <TwoPartHeaderCell
            classNames={PM_COL_SPAN_SUBHEADER_CELL}
            secondPartTitle="Last 3 PM Scores"
            spanClassNames={SORTING_SPACE}
        />,
    );

export const getStudentsRowObjects = (studentsRowObjects, studentRows) => {
    const studentRowObjects = studentRows.map(studentRow => ({
        rowObject: studentRow,
    }));
    return studentsRowObjects.push(studentRowObjects);
};

export const getRowsForGrade = (studentsRowObjects, rowObject) => {
    return [
        [
            {
                isSubheader: true,
                rowObject,
            },
        ],
        ...studentsRowObjects,
    ];
};
